import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import debounce from "lodash.debounce";

import { logout } from "../../features/userSlice";
import { useFetchCategories } from "../../hooks/useCategory";
import {
  useFetchPredefinedRoutesData,
  useRemovePredefinedRoute,
} from "../../hooks/usePredefinedRoutes";

import ModalWindow from "../../Components/ModalWindow/ModalWindow";
import PredefinedRoutePreview from "./PredefinedRoutePreview";
import "../LocationsPage/Locations.css";
import AdminNavigationLinks from "../../Components/AdminNavigationLinks/AdminNavigationLinks";

const ITEMS_PER_PAGE = 100;

const PredefinedRoutes = () => {
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [searchCategory, setSearchCategory] = useState("");
  const [deleteRoute, setDeleteRoute] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRefetchingRoutes, setIsRefetchingRoutes] = useState(false);

  const dispatch = useDispatch();

  const { data: categories = [], isLoading: isLoadingCategories } =
    useFetchCategories();

  const { predefinedRoutes, isLoading, refetch } = useFetchPredefinedRoutesData(
    debouncedSearchQuery,
    searchCategory,
    1,
    ITEMS_PER_PAGE,
  );

  const { mutate: deletingRoute } = useRemovePredefinedRoute();

  const confirmDeletingRoute = () => {
    if (!deleteRoute?.id) return;

    setIsDeleting(true);
    deletingRoute(deleteRoute.id, {
      onSettled: () => {
        setIsDeleting(false);
        setDeleteRoute(null);
      },
    });
  };

  const debouncedSetSearchQuery = useCallback(
    debounce((value) => {
      setDebouncedSearchQuery(value);
    }, 500),
    [],
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSetSearchQuery(value);
  };

  const handleLogOut = (e) => {
    e.preventDefault();

    try {
      sessionStorage.removeItem("user");
      dispatch(logout());
    } catch (error) {
      console.log(error.message);
    }
  };

  const handleRefetchRoutes = async () => {
    setIsRefetchingRoutes(true);
    try {
      await refetch();
    } catch (error) {
      console.log(error);
    } finally {
      setIsRefetchingRoutes(false);
    }
  };

  if (isLoading || isLoadingCategories) {
    return (
      <div className="invisibleWrapper">
        <p className="loadingText">Loading...</p>
      </div>
    );
  }

  return (
    <div className="locationsWrapper">
      <div className="titleContainer">
        <p className="link">
          <Link to="/predefined-routes/create">Dodaj novu rutu</Link>
        </p>

        <h1>Predefined Routes</h1>
        <button className="logout" onClick={handleLogOut}>
          Izloguj se
        </button>
      </div>
      <AdminNavigationLinks />
      <div>
        <button onClick={handleRefetchRoutes} style={{ margin: "0" }}>
          Osvezi rute
        </button>
      </div>

      <div className="searchboxWrapper">
        <div>
          <label>Pretraga ruta se automatski pokrece nakon unosa teksta</label>
          <input
            type="text"
            placeholder="Unesi naziv rute..."
            value={searchValue}
            onChange={handleSearchChange}
          />
        </div>

        <div className="selectCategoryContainer">
          <label>Izaberi kategoriju</label>
          <select
            value={searchCategory}
            onChange={(e) => {
              setSearchCategory(e.target.value);
            }}
          >
            <option value="">Sve kategorije</option>
            {categories.map((category) => (
              <option key={category.id} value={category.nameEnglish}>
                {category.nameLatin}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="locationsContainer">
        {(predefinedRoutes || []).map((route) => (
          <PredefinedRoutePreview
            key={route.id}
            route={route}
            setDeleteRoute={setDeleteRoute}
            categories={categories}
          />
        ))}
      </div>

      {!isLoading && (!predefinedRoutes || predefinedRoutes.length === 0) && (
        <div style={{ marginTop: "20px" }}>
          <p>Nema ruta za prikaz.</p>
        </div>
      )}

      {deleteRoute && (
        <ModalWindow
          title="Da li ste sigurni da zelite da obrisete rutu?"
          setDeleteData={setDeleteRoute}
          confirmDeletingData={confirmDeletingRoute}
        />
      )}

      {(isLoading || isDeleting || isRefetchingRoutes) && (
        <div className="invisibleWrapper">
          <p className="loadingText">Loading...</p>
        </div>
      )}
    </div>
  );
};

export default PredefinedRoutes;
